/**
 * Pure helpers for analyzing the staging-merge graph on the Bulk Similarity
 * Triage Page (`BulkSimilarityPage.jsx`).
 *
 * Extracted to its own module so the unit tests in
 * `src/pages/admin/__tests__/analyzeMergeOps.test.js` can import it
 * without dragging in the full BulkSimilarityPage component graph
 * (react-router-dom, axios, lucide-react, etc.).
 */

/**
 * Analyze the admin's pending merge decisions for cycles and transitive chains.
 *
 * Given a `rowState` map ({stagingId: {action, mergeTargetId, mergeTargetSource}}),
 * builds the merge graph and returns:
 *   • `cycles`       — list of cycles as arrays of staging IDs
 *                      (e.g. [['Q1', 'Q20', 'Q1']]). Each entry is a cycle path.
 *   • `chains`       — list of transitive chains as objects
 *                      { source, path: [source, ...intermediates, terminal],
 *                        finalTarget, finalTargetSource }
 *   • `orderedOps`   — staging-merge ops in safe topological order.
 *                      A source X (X→Y) must be applied BEFORE Y is itself
 *                      merged into something else, otherwise Y gets deleted
 *                      first and X's merge fails with 404.
 *   • `approvedOps`  — staging→approved merge ops (always safe; appended last
 *                      per source so any inbound staging-staging merge into
 *                      that source has already absorbed its history).
 *   • `hasErrors`    — true iff any cycle was detected (admin must fix first).
 *
 * CALLER CONTRACT: callers MUST check `hasErrors` BEFORE checking emptiness
 * of `orderedOps` / `approvedOps`. When `hasErrors === true`, the analyzer
 * deliberately returns orderedOps=[] and approvedOps=[] (refuses to compute
 * a topological order through a cycle). Treating an empty ops array as
 * "nothing to apply" without checking hasErrors first will silently hide
 * the cycle from the admin.
 */
export function analyzeMergeOps(rowState) {
  // Build adjacency map for staging→staging edges. Approved-target merges
  // are tracked separately because approved questions can never be sources.
  const edges = new Map(); // sourceId -> { target: targetId }
  const approvedOps = []; // staging→approved merges
  for (const [sid, s] of Object.entries(rowState)) {
    if (s.action !== 'merge' || !s.mergeTargetId) continue;
    if (s.mergeTargetSource === 'staging') {
      edges.set(sid, { target: s.mergeTargetId });
    } else {
      approvedOps.push({ source: sid, target: s.mergeTargetId });
    }
  }

  // --- Cycle detection (DFS with three-color marking) ---
  const cycles = [];
  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = new Map();
  for (const n of edges.keys()) color.set(n, WHITE);

  function dfs(node, path) {
    color.set(node, GRAY);
    path.push(node);
    const edge = edges.get(node);
    if (edge) {
      const next = edge.target;
      if (color.get(next) === GRAY) {
        // Cycle found — slice from the back-edge's destination
        const start = path.indexOf(next);
        cycles.push(path.slice(start).concat([next]));
      } else if (color.get(next) === WHITE) {
        dfs(next, path);
      }
    }
    color.set(node, BLACK);
    path.pop();
  }
  for (const n of edges.keys()) {
    if (color.get(n) === WHITE) dfs(n, []);
  }

  // If cycles exist, refuse to compute chains/order — caller must surface
  // the error and block apply until the admin breaks the cycle.
  if (cycles.length > 0) {
    return { cycles, chains: [], orderedOps: [], approvedOps: [], hasErrors: true };
  }

  // --- Chain resolution: for each source, walk the path to terminal ---
  const chains = [];
  for (const source of edges.keys()) {
    const path = [source];
    const seen = new Set([source]);
    let cur = edges.get(source).target;
    while (edges.has(cur) && !seen.has(cur)) {
      path.push(cur);
      seen.add(cur);
      cur = edges.get(cur).target;
    }
    // `cur` is now the terminal staging node. Check whether IT has an
    // approved-target merge — that becomes the chain's true final landing.
    path.push(cur);
    const terminalState = rowState[cur];
    let finalTarget = cur;
    let finalTargetSource = 'staging';
    if (terminalState && terminalState.action === 'merge'
        && terminalState.mergeTargetSource === 'approved'
        && terminalState.mergeTargetId) {
      finalTarget = terminalState.mergeTargetId;
      finalTargetSource = 'approved';
    }
    if (path.length > 2 || finalTargetSource === 'approved') {
      // Chain = anything where intent != direct staging→staging merge.
      // path.length > 2 means there's at least one intermediate;
      // finalTargetSource==='approved' means the terminal itself also merges.
      chains.push({ source, path, finalTarget, finalTargetSource });
    }
  }

  // --- Topological sort (Kahn) for staging→staging ops ---
  const indegree = new Map();
  for (const n of edges.keys()) indegree.set(n, 0);
  for (const { target } of edges.values()) {
    if (edges.has(target)) {
      indegree.set(target, indegree.get(target) + 1);
    }
  }
  const queue = [];
  for (const [n, d] of indegree) if (d === 0) queue.push(n);
  const orderedOps = [];
  while (queue.length) {
    const src = queue.shift();
    const edge = edges.get(src);
    orderedOps.push({ source_staging_id: src, target_staging_id: edge.target });
    if (edges.has(edge.target)) {
      indegree.set(edge.target, indegree.get(edge.target) - 1);
      if (indegree.get(edge.target) === 0) queue.push(edge.target);
    }
  }

  return { cycles, chains, orderedOps, approvedOps, hasErrors: false };
}
